import {
  VIEWPORT_DEFAULT_ZOOM,
  VIEWPORT_MAX_ZOOM,
  VIEWPORT_MIN_ZOOM,
} from '../utils/constants.js';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface ViewportState {
  readonly panX: number;
  readonly panY: number;
  readonly zoom: number;
}

/**
 * 2D viewport transform separating world space (the pannable/zoomable chain)
 * from screen space (racks, overlays, drag ghost). World point W maps to
 * screen point S via:  S = W * zoom + pan.
 *
 * Pan + zoom are mutated in-place; the render loop reads the current state
 * each frame. A `userControlled` flag captures whether the player has
 * manually panned/zoomed since the last fit — auto-fit-on-chain-growth
 * stays out of the way once the user takes the wheel.
 */
export class Viewport {
  private panX = 0;
  private panY = 0;
  private zoom = VIEWPORT_DEFAULT_ZOOM;
  private userControlled = false;

  // Tween targets — when set, the per-frame step interpolates toward them.
  private tween: {
    fromPanX: number;
    fromPanY: number;
    fromZoom: number;
    toPanX: number;
    toPanY: number;
    toZoom: number;
    startedAt: number;
    durationMs: number;
  } | null = null;

  getState(): ViewportState {
    return { panX: this.panX, panY: this.panY, zoom: this.zoom };
  }

  isUserControlled(): boolean {
    return this.userControlled;
  }

  markUserControlled(): void {
    this.userControlled = true;
    this.tween = null;
  }

  clearUserControlled(): void {
    this.userControlled = false;
  }

  /** Apply the viewport transform to a 2D context. Caller saves/restores. */
  applyTo(ctx: CanvasRenderingContext2D): void {
    ctx.translate(this.panX, this.panY);
    ctx.scale(this.zoom, this.zoom);
  }

  screenToWorld(p: Point): Point {
    return { x: (p.x - this.panX) / this.zoom, y: (p.y - this.panY) / this.zoom };
  }

  worldToScreen(p: Point): Point {
    return { x: p.x * this.zoom + this.panX, y: p.y * this.zoom + this.panY };
  }

  /**
   * Multiplicative zoom around a screen-space anchor. Standard
   * map-style zoom: the world point under the anchor stays under
   * the anchor after the zoom.
   */
  zoomAt(anchorScreen: Point, factor: number): void {
    const newZoom = clamp(this.zoom * factor, VIEWPORT_MIN_ZOOM, VIEWPORT_MAX_ZOOM);
    if (newZoom === this.zoom) return;
    const k = newZoom / this.zoom;
    this.panX = anchorScreen.x - (anchorScreen.x - this.panX) * k;
    this.panY = anchorScreen.y - (anchorScreen.y - this.panY) * k;
    this.zoom = newZoom;
    this.tween = null;
  }

  panBy(dx: number, dy: number): void {
    this.panX += dx;
    this.panY += dy;
    this.tween = null;
  }

  /** Snap to an exact viewport state. Cancels any in-flight tween. */
  resetTo(panX: number, panY: number, zoom: number): void {
    this.panX = panX;
    this.panY = panY;
    this.zoom = clamp(zoom, VIEWPORT_MIN_ZOOM, VIEWPORT_MAX_ZOOM);
    this.tween = null;
  }

  /**
   * Compute the (panX, panY, zoom) that frames `bounds` inside `screenRect`
   * with `padding` world-px of breathing room on each side. The computed
   * zoom is capped at `VIEWPORT_DEFAULT_ZOOM` so a small chain (or the
   * empty-chain start state) doesn't get aggressively zoomed in past the
   * comfortable default view — auto-fit only ever zooms OUT to keep a
   * large chain framed. Manual wheel/pinch can still zoom up to
   * `VIEWPORT_MAX_ZOOM` explicitly.
   */
  computeFit(bounds: Rect, screenRect: Rect, padding: number): ViewportState {
    const bw = Math.max(1, bounds.maxX - bounds.minX);
    const bh = Math.max(1, bounds.maxY - bounds.minY);
    const sw = Math.max(1, screenRect.maxX - screenRect.minX);
    const sh = Math.max(1, screenRect.maxY - screenRect.minY);
    const zoomFit = Math.min(sw / (bw + padding * 2), sh / (bh + padding * 2));
    const zoom = clamp(Math.min(zoomFit, VIEWPORT_DEFAULT_ZOOM), VIEWPORT_MIN_ZOOM, VIEWPORT_DEFAULT_ZOOM);
    const worldCx = (bounds.minX + bounds.maxX) / 2;
    const worldCy = (bounds.minY + bounds.maxY) / 2;
    const screenCx = (screenRect.minX + screenRect.maxX) / 2;
    const screenCy = (screenRect.minY + screenRect.maxY) / 2;
    const panX = screenCx - worldCx * zoom;
    const panY = screenCy - worldCy * zoom;
    return { panX, panY, zoom };
  }

  /** Animate to the framed viewport. `now` is wall-clock ms. */
  fitTo(bounds: Rect, screenRect: Rect, padding: number, durationMs: number, now: number): void {
    const target = this.computeFit(bounds, screenRect, padding);
    if (durationMs <= 0) {
      this.resetTo(target.panX, target.panY, target.zoom);
      return;
    }
    this.tween = {
      fromPanX: this.panX,
      fromPanY: this.panY,
      fromZoom: this.zoom,
      toPanX: target.panX,
      toPanY: target.panY,
      toZoom: target.zoom,
      startedAt: now,
      durationMs,
    };
  }

  /** Advance any in-flight tween. Call once per frame with the current wall-clock ms. */
  step(now: number): void {
    const t = this.tween;
    if (t === null) return;
    const k = Math.min(1, (now - t.startedAt) / t.durationMs);
    const e = easeOutCubic(k);
    this.panX = t.fromPanX + (t.toPanX - t.fromPanX) * e;
    this.panY = t.fromPanY + (t.toPanY - t.fromPanY) * e;
    this.zoom = t.fromZoom + (t.toZoom - t.fromZoom) * e;
    if (k >= 1) this.tween = null;
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}
